import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, Menu, X, ChevronDown, User, LogOut, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import CurrencySwitcher from "@/components/CurrencySwitcher";

const NAV = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    {
        label: "Services",
        to: "/services",
        children: [
            { label: "Home Detergents", to: "/services#detergents" },
            { label: "ICT Consultancy", to: "/services#ict" },
            { label: "Software Development", to: "/services#software" },
        ],
    },
    { label: "Products", to: "/products" },
    { label: "Careers", to: "/careers" },
    { label: "News", to: "/news" },
    { label: "Downloads", to: "/downloads" },
    { label: "Gallery", to: "/gallery" },
    { label: "Contact", to: "/contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(null);
    const { user, logout } = useAuth();
    const { count } = useCart();
    const { count: wishCount } = useWishlist();
    const nav = useNavigate();

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-border"
        >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                <div className="flex items-center justify-between h-16 md:h-20">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
                        <img src="/logo.png" alt="Peppa Target Services" className="h-12 w-auto" />
                        <div className="leading-tight hidden sm:block">
                            <div className="font-display font-bold text-lg tracking-tight">Peppa Target</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Services Ltd</div>
                        </div>
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV.map((item) => (
                            <div
                                key={item.label}
                                className="relative"
                                onMouseEnter={() => item.children && setMenuOpen(item.label)}
                                onMouseLeave={() => setMenuOpen(null)}
                            >
                                <NavLink
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `px-3 py-2 text-sm font-medium flex items-center gap-1 transition-colors ${
                                            isActive ? "text-primary" : "text-ink hover:text-primary"
                                        }`
                                    }
                                >
                                    {item.label}
                                    {item.children && <ChevronDown className="w-3 h-3" />}
                                </NavLink>
                                {item.children && menuOpen === item.label && (
                                    <div className="absolute top-full left-0 mt-0 bg-white border border-border shadow-lg min-w-[240px] py-2">
                                        {item.children.map((c) => (
                                            <Link
                                                key={c.label}
                                                to={c.to}
                                                className="block px-4 py-2 text-sm hover:bg-muted hover:text-primary transition-colors"
                                                data-testid={`submenu-${c.label.toLowerCase().replace(/\s/g, '-')}`}
                                            >
                                                {c.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <div className="hidden md:flex items-center mr-2 border-r border-border pr-2">
                            <CurrencySwitcher />
                        </div>
                        <Link
                            to="/wishlist"
                            className="relative p-2 hover:text-primary transition-colors hidden md:inline-flex"
                            data-testid="header-wishlist-link"
                            aria-label="Wishlist"
                        >
                            <Heart className="w-5 h-5" />
                            {wishCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                                    {wishCount}
                                </span>
                            )}
                        </Link>
                        <Link
                            to="/cart"
                            className="relative p-2 hover:text-primary transition-colors"
                            data-testid="header-cart-link"
                        >
                            <ShoppingCart className="w-5 h-5" />
                            {count > 0 && (
                                <span
                                    className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full"
                                    data-testid="cart-count-badge"
                                >
                                    {count}
                                </span>
                            )}
                        </Link>

                        {user ? (
                            <div className="hidden md:flex items-center gap-2">
                                <Link
                                    to={user.role === "admin" ? "/admin" : "/profile"}
                                    className="flex items-center gap-2 px-3 py-2 text-sm hover:text-primary transition-colors"
                                    data-testid="header-account-link"
                                >
                                    <User className="w-4 h-4" />
                                    <span className="font-medium">{user.name.split(" ")[0]}</span>
                                </Link>
                                <button
                                    onClick={() => { logout(); nav("/"); }}
                                    className="p-2 text-muted-foreground hover:text-primary transition-colors"
                                    data-testid="header-logout-btn"
                                    title="Sign out"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                className="hidden md:inline-flex items-center px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-primary-hover transition-colors"
                                data-testid="header-login-btn"
                            >
                                Sign in
                            </Link>
                        )}

                        <button
                            className="lg:hidden p-2"
                            onClick={() => setOpen(!open)}
                            data-testid="mobile-menu-toggle"
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div className="lg:hidden bg-white border-t border-border" data-testid="mobile-menu">
                    <div className="px-4 py-4 space-y-1">
                        {NAV.map((item) => (
                            <Link
                                key={item.label}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                className="block px-3 py-2 text-sm font-medium hover:bg-muted hover:text-primary transition-colors"
                                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </Link>
                        ))}
                        {!user ? (
                            <Link
                                to="/login"
                                onClick={() => setOpen(false)}
                                className="block mt-2 px-3 py-2 text-sm font-semibold bg-primary text-white"
                                data-testid="mobile-login-link"
                            >
                                Sign in
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to={user.role === "admin" ? "/admin" : "/profile"}
                                    onClick={() => setOpen(false)}
                                    className="block px-3 py-2 text-sm font-semibold text-primary"
                                >
                                    My Account
                                </Link>
                                <button
                                    onClick={() => { logout(); setOpen(false); nav("/"); }}
                                    className="block w-full text-left px-3 py-2 text-sm font-semibold text-primary"
                                >
                                    Sign out
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
